import { Fragment, useId } from 'react';
import Beacon from '../Beacon.jsx';
import { IconChevron, IconExternal, IconSettings } from '../icons.jsx';
import { hasNamedTenantAction } from '../../product-policy.js';
import { CampaignManagementControls } from './CampaignManagementControls.jsx';
import { Pill } from '../primitives.jsx';
import { CampaignBillingAccessNotice } from './CampaignBillingAccess.jsx';

// OGC-445: campaign status vocabulary renders through the shared Pill primitive
// (uppercase, 12px on these surfaces like Leads). Tones: live = Active, warn =
// Paused / delivery attention, accent = Drafts & review, neutral = Archived.
const STATUS_TONES = Object.freeze({
  active: 'live',
  paused: 'warn',
  draft: 'accent',
  awaiting_approval: 'accent',
  approved: 'accent',
  publishing: 'accent',
  failed: 'warn',
  partial: 'warn',
  completed: 'neutral',
  archived: 'neutral',
});
export function campaignStatusTone(status) {
  return STATUS_TONES[status] || 'neutral';
}
// Long labels ("Check delivery", "In review" at 1051-1250px) wrap inside the OGC-395
// status track: the wrap is intentional (whole words, 1.2 leading), and a single
// line stays 26px tall like the Leads row pill.
const CAMPAIGN_PILL_STYLE = Object.freeze({ fontSize: 12, lineHeight: 1.2, padding: '5px 9px', maxWidth: '100%', textAlign: 'center', hyphens: 'none', overflowWrap: 'normal' });
export function CampaignStatePill({ tone = 'neutral', className = '', style, children, ...rest }) {
  return (
    <Pill tone={tone} className={className} style={{ ...CAMPAIGN_PILL_STYLE, ...style }} {...rest}>
      {children}
    </Pill>
  );
}

const ALL_METRICS = Object.freeze([
  Object.freeze({ key: 'spend', label: 'Spend', kind: 'money' }),
  Object.freeze({ key: 'reach', label: 'Reach', kind: 'integer' }),
  Object.freeze({ key: 'frequency', label: 'Frequency', kind: 'frequency' }),
  Object.freeze({ key: 'impressions', label: 'Impressions', kind: 'integer' }),
  Object.freeze({ key: 'ctr', label: 'CTR', kind: 'percent' }),
  Object.freeze({ key: 'metaReportedLeads', label: 'Leads', kind: 'integer', ariaLabel: 'Meta-reported leads' }),
  Object.freeze({ key: 'costPerLead', label: 'Cost / lead', kind: 'money' }),
]);
export const CAMPAIGN_PRIMARY_METRICS = Object.freeze([
  { key: 'metaReportedLeads', label: 'Leads', kind: 'integer', ariaLabel: 'Meta-reported leads' },
  { key: 'costPerLead', label: 'Cost / lead', kind: 'money' },
  { key: 'spend', label: 'Spend', kind: 'money' },
  { key: 'ctr', label: 'CTR', kind: 'percent' },
]);
const METRIC_AVAILABILITY = new Set(['available', 'not_applicable', 'unavailable']);
const PERFORMANCE_AVAILABILITY = new Set(['available', 'partial', 'no_data', 'unavailable']);
const BATCH_AVAILABILITY = new Set(['available', 'partial']);
const FRESHNESS_STATES = new Set(['available', 'no_data', 'stale', 'unavailable']);
const ROW_SUMMARY_SCHEMA_VERSION = 'curated.campaign.row-summaries.v1';
const SAFE_PARTIAL_CODE = /^[a-z][a-z0-9_]{0,95}$/;
const SAFE_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_PRESET_LABELS = Object.freeze({
  today: 'Today',
  last_7d: 'Last 7 days',
  last_30d: 'Last 30 days',
  maximum: 'Lifetime',
});
const CAMPAIGN_STATES = new Set([
  'draft', 'awaiting_approval', 'approved', 'publishing', 'paused', 'active',
  'partial', 'failed', 'completed', 'archived',
]);

function exactText(value, maximumLength = 240) {
  if (typeof value !== 'string') return '';
  const normalized = value.trim();
  return normalized && normalized.length <= maximumLength ? normalized : '';
}

function label(value) {
  if (typeof value === 'string') return exactText(value);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  return exactText(value.label) || exactText(value.name);
}

function invalidRowSummaries() {
  const error = new Error('Campaign row summary response is invalid.');
  error.code = 'campaign_row_summaries_invalid';
  return error;
}

function safeDate(value) {
  const normalized = exactText(value, 10);
  if (!SAFE_DATE.test(normalized)) return null;
  const parsed = new Date(`${normalized}T00:00:00.000Z`);
  return Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === normalized
    ? normalized
    : null;
}

function safeTimestamp(value) {
  const normalized = exactText(value, 64);
  return normalized && Number.isFinite(Date.parse(normalized)) ? normalized : null;
}

function normalizeReportingPeriod(value, expectedDatePreset) {
  const period = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  if (exactText(period.datePreset, 32) !== expectedDatePreset) throw invalidRowSummaries();
  const since = period.since === null ? null : safeDate(period.since);
  const until = period.until === null ? null : safeDate(period.until);
  const coherentRange = since && until && since <= until;
  return Object.freeze({
    datePreset: expectedDatePreset,
    since: coherentRange ? since : null,
    until: coherentRange ? until : null,
  });
}

function normalizeFreshness(value) {
  const freshness = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const state = exactText(freshness.state, 32);
  return Object.freeze({
    state: FRESHNESS_STATES.has(state) ? state : 'unavailable',
    retrievedAt: freshness.retrievedAt === null ? null : safeTimestamp(freshness.retrievedAt),
    dataThrough: freshness.dataThrough === null ? null : safeDate(freshness.dataThrough),
  });
}

export function normalizeCampaignRowSummaryBatch(response, expectedDatePreset = 'last_30d') {
  const availability = exactText(response?.availability, 32);
  const partialData = response?.partialData;
  if (
    response?.schemaVersion !== ROW_SUMMARY_SCHEMA_VERSION
    || response?.datePreset !== expectedDatePreset
    || !BATCH_AVAILABILITY.has(availability)
    || !Array.isArray(partialData)
    || partialData.length > 20
  ) throw invalidRowSummaries();
  const partialCodes = [];
  const seen = new Set();
  for (const value of partialData) {
    const code = exactText(value?.code, 96);
    if (!SAFE_PARTIAL_CODE.test(code) || seen.has(code)) throw invalidRowSummaries();
    seen.add(code);
    partialCodes.push(code);
  }
  if (
    (availability === 'available' && partialCodes.length > 0)
    || (availability === 'partial' && partialCodes.length === 0)
  ) throw invalidRowSummaries();
  return Object.freeze({
    availability,
    datePreset: expectedDatePreset,
    partialCodes: Object.freeze(partialCodes),
  });
}

function normalizeMetric(value) {
  const availability = METRIC_AVAILABILITY.has(exactText(value?.availability, 32))
    ? value.availability
    : 'unavailable';
  const numeric = typeof value?.value === 'number' && Number.isFinite(value.value) && value.value >= 0
    ? value.value
    : null;
  return Object.freeze({
    value: numeric,
    availability: numeric === null && availability === 'available' ? 'unavailable' : availability,
    unit: exactText(value?.unit, 16) || null,
  });
}

function normalizeBudget(settings) {
  const value = settings?.monthlyBudget || settings?.budget || {};
  const monthlyMajor = /^\d+(?:\.\d{1,2})?$/.test(exactText(value.monthlyMajor, 32))
    ? value.monthlyMajor
    : null;
  const currencyText = exactText(value.currency, 3).toUpperCase();
  return Object.freeze({
    monthlyMajor,
    currency: /^[A-Z]{3}$/.test(currencyText) ? currencyText : null,
    canUpdate: value.canUpdate === true,
  });
}

function normalizeSettings(value) {
  const settings = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const status = exactText(settings.status, 32).toLowerCase();
  const renovationTypes = Array.isArray(settings.renovationTypes)
    ? settings.renovationTypes.slice(0, 20).map(label).filter(Boolean)
    : [];
  return Object.freeze({
    status: CAMPAIGN_STATES.has(status) ? status : null,
    renovationTypes: Object.freeze(renovationTypes),
    selectedAdCount: Number.isSafeInteger(settings.selectedAdCount) && settings.selectedAdCount >= 0 && settings.selectedAdCount <= 100 ? settings.selectedAdCount : null,
    adAccount: label(settings.adAccount) || null,
    instantForm: label(settings.instantForm) || null,
    instagramAccount: label(settings.instagramAccount) || null,
    instagramSelected: typeof settings.instagramSelected === 'boolean'
      ? settings.instagramSelected
      : null,
    facebookPage: label(settings.facebookPage) || null,
    targetArea: label(settings.targetArea) || null,
    monthlyBudget: normalizeBudget(settings),
  });
}

function normalizePerformance(value, expectedDatePreset) {
  const performance = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const availabilityText = exactText(performance.availability, 32);
  const metrics = performance.metrics && typeof performance.metrics === 'object'
    ? performance.metrics
    : {};
  return Object.freeze({
    availability: PERFORMANCE_AVAILABILITY.has(availabilityText) ? availabilityText : 'unavailable',
    metrics: Object.freeze(Object.fromEntries(
      ALL_METRICS.map(({ key }) => [key, normalizeMetric(metrics[key])]),
    )),
    period: normalizeReportingPeriod(performance.period, expectedDatePreset),
    freshness: normalizeFreshness(performance.freshness),
    account: performance.account?.source === 'meta_ad_account' ? { currency: /^[A-Z]{3}$/.test(performance.account.currency || '') ? performance.account.currency : null, timezone: exactText(performance.account.timezone, 100) || null } : null,
  });
}

// OGC-490: the server's closed per-row Meta-access reason. Anything else,
// including a state paired with the wrong reason, reads as no reason.
const ROW_META_ACCESS = Object.freeze({ republish_needed: 'original_unprovable', refreshing: 'refreshing' });
function normalizeMetaAccess(value) {
  const state = exactText(value?.state, 32);
  return Object.hasOwn(ROW_META_ACCESS, state) && ROW_META_ACCESS[state] === value?.reasonCode
    ? Object.freeze({ state, reasonCode: value.reasonCode })
    : null;
}

export function normalizeCampaignRowSummaries(response, expectedCampaignIds, expectedDatePreset = 'last_30d') {
  const expectedIds = new Set((Array.isArray(expectedCampaignIds) ? expectedCampaignIds : [])
    .filter((value) => typeof value === 'string' && value.trim() === value && value));
  normalizeCampaignRowSummaryBatch(response, expectedDatePreset);
  if (!Array.isArray(response?.summaries)) throw invalidRowSummaries();
  const summaries = response.summaries;
  const normalized = new Map();
  for (const value of summaries.slice(0, 200)) {
    const campaignId = exactText(value?.campaignId, 128);
    if (!expectedIds.has(campaignId) || normalized.has(campaignId)) continue;
    normalized.set(campaignId, Object.freeze({
      campaignId,
      settings: normalizeSettings(value.settings),
      performance: normalizePerformance(value.performance, expectedDatePreset),
      metaAccess: normalizeMetaAccess(value.metaAccess),
    }));
  }
  return normalized;
}

function number(value, options = {}) {
  return new Intl.NumberFormat(undefined, options).format(value);
}

export function formatCampaignRowMetric(metric, kind, currency = '', unavailable = 'N/A') {
  if (!metric || metric.value === null || metric.availability !== 'available') return unavailable;
  if (kind === 'money') {
    const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : '';
    return safeCurrency
      ? number(metric.value, { style: 'currency', currency: safeCurrency, maximumFractionDigits: 2 })
      : number(metric.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (kind === 'percent') return `${number(metric.value, { maximumFractionDigits: 2 })}%`;
  if (kind === 'frequency') return number(metric.value, { maximumFractionDigits: 2 });
  return number(metric.value, { maximumFractionDigits: 0 });
}

export function CampaignMetricValue({ metric, kind, currency, unavailable = 'N/A' }) {
  const formatted = formatCampaignRowMetric(metric, kind, currency, unavailable);
  const group = new Intl.NumberFormat().formatToParts(12345).find((part) => part.type === 'group')?.value;
  if (!group || !formatted.includes(group)) return formatted;
  // Prefer complete digit groups when a narrow cell needs to wrap a large value.
  const groups = formatted.split(group);
  return groups.map((value, index) => <Fragment key={index}>{value}{index < groups.length - 1 ? <>{group}<wbr /></> : null}</Fragment>);
}

export function campaignFullDetailDestination(campaignId) {
  if (typeof campaignId !== 'string' || !campaignId || campaignId.trim() !== campaignId) return null;
  return `campaigns/${encodeURIComponent(campaignId)}`;
}

export function nextExpandedCampaignId(currentCampaignId, requestedCampaignId) {
  const requested = exactText(requestedCampaignId, 128);
  return !requested || requested === exactText(currentCampaignId, 128) ? '' : requested;
}

function compactDate(value, includeTime = false) {
  const parsed = includeTime
    ? new Date(value || '')
    : new Date(`${value || ''}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.valueOf())) return '';
  return new Intl.DateTimeFormat(undefined, includeTime
    ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(parsed);
}

export function campaignReportingPeriod(summary, requestedDatePreset = 'last_30d') {
  const period = summary?.performance?.period;
  if (period?.since && period?.until) {
    const since = new Date(`${period.since}T00:00:00.000Z`);
    const until = new Date(`${period.until}T00:00:00.000Z`);
    const formatter = new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
    if (Number.isFinite(since.valueOf()) && Number.isFinite(until.valueOf())) {
      return typeof formatter.formatRange === 'function'
        ? formatter.formatRange(since, until)
        : `${formatter.format(since)}–${formatter.format(until)}`;
    }
  }
  return DATE_PRESET_LABELS[period?.datePreset]
    || DATE_PRESET_LABELS[requestedDatePreset]
    || 'Reporting period N/A';
}

export function campaignPerformanceFreshness(summary, summaryStatus) {
  if (!summary) {
    return Object.freeze(summaryStatus === 'loading'
      ? { key: 'loading', label: 'Loading' }
      : { key: 'unavailable', label: 'Freshness N/A' });
  }
  const freshness = summary.performance?.freshness || {};
  if (freshness.state === 'stale') return Object.freeze({ key: 'stale', label: 'Stale' });
  if (freshness.state === 'no_data') return Object.freeze({ key: 'no-data', label: 'No data' });
  if (freshness.state !== 'available') {
    return Object.freeze({ key: 'unavailable', label: 'Freshness N/A' });
  }
  if (freshness.dataThrough) {
    return Object.freeze({ key: 'available', label: `Data through ${compactDate(freshness.dataThrough)}` });
  }
  if (freshness.retrievedAt) {
    return Object.freeze({ key: 'available', label: `Updated ${compactDate(freshness.retrievedAt, true)}` });
  }
  return Object.freeze({ key: 'unavailable', label: 'Freshness N/A' });
}

export function campaignPerformanceRefreshState(summaryStatus, hasSummary) {
  if (summaryStatus === 'loading' && hasSummary) {
    return Object.freeze({ key: 'refreshing', label: 'Refreshing' });
  }
  if (summaryStatus === 'error' && hasSummary) {
    return Object.freeze({ key: 'stale', label: 'Refresh failed' });
  }
  return null;
}

export function campaignPerformanceCompleteness(summary) {
  return summary?.performance?.availability === 'partial'
    ? Object.freeze({ key: 'partial', label: 'Partial metrics' })
    : null;
}

function statusLabel(status) {
  const labels = {
    draft: 'Draft',
    awaiting_approval: 'Ready to review',
    approved: 'Approved',
    publishing: 'Publishing',
    paused: 'Paused',
    active: 'Active',
    partial: 'Partial',
    failed: 'Failed',
    completed: 'Completed',
    archived: 'Archived',
  };
  return labels[status] || 'N/A';
}

function dateTime(value) {
  const parsed = new Date(value || '');
  if (!Number.isFinite(parsed.valueOf())) return 'N/A';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function formatBudget(value) {
  if (!value?.monthlyMajor) return 'N/A';
  const amount = Number(value.monthlyMajor);
  if (!Number.isFinite(amount)) return 'N/A';
  return value.currency
    ? number(amount, { style: 'currency', currency: value.currency, maximumFractionDigits: 2 })
    : number(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CampaignPerformanceContext({ datePreset, summary, summaryStatus }) {
  const freshness = campaignPerformanceFreshness(summary, summaryStatus);
  const refreshState = campaignPerformanceRefreshState(summaryStatus, Boolean(summary));
  const completeness = campaignPerformanceCompleteness(summary);
  const period = campaignReportingPeriod(summary, datePreset);
  const currency = campaignReportingCurrency(summary);
  return (
    <div className="bl-campaign-performance-period">
      <span>{period}{summary?.performance?.account?.timezone ? ` · ${summary.performance.account.timezone}` : ' · Timezone unavailable'}{currency ? ` · ${currency}` : ' · Currency unavailable'}</span>
      <span className="bl-campaign-performance-truth">
        <strong className={`is-${freshness.key}`}>{freshness.label}</strong>
        {refreshState && <strong className={`is-${refreshState.key}`}>{refreshState.key === 'refreshing' ? <Beacon size="inline" tone="neutral" delayMs={0} slowAfterMs={null} /> : null}{refreshState.key === 'refreshing' ? ' ' : ''}{refreshState.label}</strong>}
        {completeness && <strong className={`is-${completeness.key}`}>{completeness.label}</strong>}
      </span>
    </div>
  );
}

export function CampaignMetricStrip({ campaignName, datePreset, summary, summaryStatus, compact = false }) {
  const loading = summaryStatus === 'loading';
  const showSkeleton = loading && !summary;
  const period = campaignReportingPeriod(summary, datePreset);
  const metrics = summary?.performance?.metrics || {};
  const currency = campaignReportingCurrency(summary);
  return (
    <div className="bl-campaign-row-performance" aria-label={`${campaignName} performance · ${period}`} aria-busy={loading ? 'true' : 'false'}>
      {!compact && <CampaignPerformanceContext datePreset={datePreset} summary={summary} summaryStatus={summaryStatus} />}
      <div className="bl-campaign-row-metrics">
        {CAMPAIGN_PRIMARY_METRICS.map(({ key, label: metricLabel, kind, ariaLabel }) => (
          <div key={key} className="bl-campaign-row-metric" aria-label={ariaLabel}>
            <span>{metricLabel}</span>
            {showSkeleton
              ? <i className="bl-campaign-metric-skeleton" aria-hidden="true" />
              : <strong aria-label={metrics[key]?.availability !== 'available' || (kind === 'money' && !currency) ? `${metricLabel}: unavailable` : undefined}>{kind === 'money' && !currency ? (compact ? '—' : 'N/A') : <CampaignMetricValue metric={metrics[key]} kind={kind} currency={currency} unavailable={compact ? '—' : 'N/A'} />}</strong>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function campaignReportingCurrency(summary) {
  const metrics = summary?.performance?.metrics || {};
  // Planning currency describes the saved budget, not Meta's reporting units.
  return [summary?.performance?.account?.currency,
    metrics.spend?.availability === 'available' ? metrics.spend?.unit : null,
    metrics.costPerLead?.availability === 'available' ? metrics.costPerLead?.unit : null]
    .find((value) => typeof value === 'string' && /^[A-Z]{3}$/.test(value)) || '';
}

export function CampaignSettings({
  budgetBusy, budgetFeedback, budgetValue, budgetInputId = 'campaign-budget',
  campaign, onBudgetChange, onBudgetSubmit, productPolicy, summary,
  formId, showSubmit = true,
  budgetAccess = null, onViewBilling, onRefreshBudget,
}) {
  const settings = summary?.settings || {};
  const budget = settings.monthlyBudget || {};
  const canUpdateBudget = budget.canUpdate === true
    && hasNamedTenantAction(productPolicy, 'campaign.budget.update')
    && typeof onBudgetSubmit === 'function';
  const amount = budgetValue ?? budget.monthlyMajor ?? '';
  const amountText = String(amount).trim();
  const validAmount = /^\d+(?:\.\d{1,2})?$/.test(amountText) && Number.isFinite(Number(amountText)) && Number(amountText) > 0;
  const currencySymbol = budget.currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: budget.currency })
      .formatToParts(0).find((part) => part.type === 'currency')?.value : '—';
  const facts = [
    ['Status', statusLabel(settings.status || campaign.status)],
    ['Renovation types', settings.renovationTypes?.join(', ') || 'N/A'],
    ['Target area', settings.targetArea || 'N/A'],
    ['Facebook Page', settings.facebookPage || 'N/A'],
    ['Instagram', settings.instagramAccount || (settings.instagramSelected === false ? 'Not selected' : 'N/A')],
    ['Instant Form', settings.instantForm || 'N/A'],
    ['Ad account', settings.adAccount || 'N/A'],
  ];
  return <form id={formId} className="bl-campaign-settings-form" onSubmit={(event) => {
    event.preventDefault();
    if (canUpdateBudget && !budgetBusy && budgetAccess?.allowed !== false) onBudgetSubmit?.();
  }}>
    <label htmlFor={budgetInputId}>Monthly campaign budget</label>
    <div className="bl-campaign-budget-input">
      <span aria-hidden="true">{currencySymbol}</span>
      <input id={budgetInputId} type="number" inputMode="decimal" min="0.01" step="0.01" required aria-label={`Monthly budget for ${campaign.name || 'campaign'}`}
        aria-describedby={`${budgetInputId}-daily`} value={amount}
        onChange={(event) => onBudgetChange?.(event.target.value)} disabled={!canUpdateBudget || budgetBusy} />
      <span>{budget.currency || 'Currency unavailable'}</span>
    </div>
    <p className="bl-campaign-budget-hint" id={`${budgetInputId}-daily`}>
      Approximately {validAmount ? formatBudget({ monthlyMajor: String(Number(amountText) / 30.4), currency: budget.currency }) : '—'}/day on Meta.<br />
      A monthly planning budget, not a hard spending cap.
    </p>
    {canUpdateBudget && <CampaignBillingAccessNotice decision={budgetAccess} onViewBilling={onViewBilling} onRefresh={onRefreshBudget} />}
    <div className="bl-campaign-settings-common">
      <h3>Common ad settings <span>{campaign.status === 'draft' ? 'Saved draft' : campaign.status === 'awaiting_approval' ? 'Ready to review' : ['approved', 'active', 'paused', 'publishing', 'completed'].includes(campaign.status) ? 'Approved setup' : 'Saved setup'}</span></h3>
      <dl className="bl-campaign-settings-facts">{facts.map(([label, value]) =>
        <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
    </div>
    <p className="bl-campaign-settings-note">{campaign.archivedAt
      ? 'Restore this campaign before changing settings.'
      : campaign.status === 'draft' ? 'Open campaign details to finish draft configuration.'
        : `Targeting and creatives are part of your ${['approved', 'active', 'paused', 'publishing', 'completed'].includes(campaign.status) ? 'approved' : 'saved'} setup. Review them in campaign details.`}</p>
    {showSubmit && canUpdateBudget && <button className="bl-campaign-settings-submit" type="submit" disabled={budgetBusy || budgetAccess?.allowed === false}>
      {budgetBusy ? 'Updating…' : 'Review budget change'}
    </button>}
    {budgetFeedback?.message && <div className={`bl-campaign-inline-feedback is-${budgetFeedback.status || 'status'}`}
      role={budgetFeedback.status === 'error' ? 'alert' : 'status'}>{budgetFeedback.message}</div>}
  </form>;
}

export function CampaignSummaryCard({
  budgetBusy = false,
  budgetFeedback = null,
  budgetValue,
  campaign,
  datePreset = 'last_30d',
  expanded = false,
  management = null,
  managementBusyAction = '',
  managementFeedback = null,
  managementStatus = '',
  onArchive,
  onBudgetChange,
  onBudgetSubmit,
  onDelete,
  onDetails,
  onRetryManagement,
  onRestore,
  onToggle,
  productPolicy,
  summary = null,
  summaryStatus = 'loading',
}) {
  const reactId = useId().replaceAll(':', '');
  const campaignName = campaign?.name || 'Curated Meta campaign';
  const titleId = `campaign-title-${reactId}`;
  const panelId = `campaign-settings-${reactId}`;
  const budgetInputId = `campaign-budget-${reactId}`;
  return (
    <article className={`bl-campaign-summary-card${expanded ? ' is-expanded' : ''}`} aria-labelledby={titleId}>
      <div className="bl-campaign-summary-heading">
        <div className="bl-campaign-summary-copy">
          <div className="bl-campaign-summary-states">
            <CampaignStatePill tone={campaignStatusTone(campaign?.status)} className={`bl-campaign-state is-${campaign?.status}`}>{statusLabel(campaign?.status)}</CampaignStatePill>
            {campaign?.archivedAt && <CampaignStatePill tone="neutral" className="bl-campaign-workspace-state">Archived in BeyondLeads</CampaignStatePill>}
          </div>
          <h3 id={titleId}>{campaignName}</h3>
          <p>Updated {dateTime(campaign?.updatedAt || campaign?.createdAt)}</p>
        </div>
        <div className="bl-campaign-summary-actions">
          <button
            type="button"
            aria-controls={panelId}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} Ad settings for ${campaignName}`}
            onClick={onToggle}
          >
            <IconSettings size={15} /> {expanded ? 'Hide settings' : 'Ad settings'} <IconChevron className="bl-campaign-disclosure-icon" size={14} />
          </button>
          <button type="button" className="is-primary" aria-label={`Details for ${campaignName}`} onClick={onDetails}>
            Details <IconExternal size={14} />
          </button>
        </div>
      </div>
      <CampaignMetricStrip
        campaignName={campaignName}
        datePreset={datePreset}
        summary={summary}
        summaryStatus={summaryStatus}
      />
      {expanded && (
        <section id={panelId} className="bl-campaign-ad-settings" role="region" aria-labelledby={`${panelId}-title`}>
          <div className="bl-campaign-ad-settings-heading">
            <div><div className="bl-kicker">Campaign setup</div><h4 id={`${panelId}-title`}>Ad settings</h4></div>
            <span>Saved campaign configuration</span>
          </div>
          <CampaignSettings
            budgetBusy={budgetBusy}
            budgetFeedback={budgetFeedback}
            budgetInputId={budgetInputId}
            budgetValue={budgetValue}
            campaign={campaign}
            onBudgetChange={onBudgetChange}
            onBudgetSubmit={onBudgetSubmit}
            productPolicy={productPolicy}
            summary={summary}
          />
          {managementStatus === 'loading' && (
            <div className="bl-campaign-management-loading"><Beacon size="control" tone="neutral" label="Checking archive and deletion eligibility…" delayMs={0} slowAfterMs={null} /></div>
          )}
          {managementStatus === 'error' && (
            <div className="bl-campaign-management-feedback is-error" role="alert">
              <span>{management
                ? 'The latest campaign management state is unavailable. Previously loaded controls are read-only until refreshed.'
                : 'Campaign management is temporarily unavailable.'}</span>
              {onRetryManagement && <button type="button" onClick={onRetryManagement}>Retry campaign controls</button>}
            </div>
          )}
          {management && (
            <CampaignManagementControls
              busyAction={managementBusyAction
                || (budgetBusy ? 'budget' : '')
                || (managementStatus === 'loading' ? 'refresh' : '')
                || (managementStatus === 'error' ? 'stale' : '')}
              feedback={managementFeedback}
              management={management}
              onArchive={onArchive}
              onDelete={onDelete}
              onDetails={onDetails}
              onRetry={managementStatus === 'error' ? undefined : onRetryManagement}
              onRestore={onRestore}
              productPolicy={productPolicy}
            />
          )}
        </section>
      )}
    </article>
  );
}
